
import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface AttachmentUploaderProps {
  entryId: string;
  userId: string;
  onUpload: () => void;
}

export const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  entryId,
  userId,
  onUpload
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    e.target.value = "";
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    if (!entryId || !userId) {
      toast.error("Entry must be saved before uploading attachments.");
      return;
    }

    setUploading(true);
    try {
      const filePath = `${userId}/${entryId}/${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("entry_attachments")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from("files").insert({
        entry_id: entryId,
        storage_path: filePath,
        filename: file.name,
        mimetype: file.type,
        size: file.size,
      });
      if (dbError) throw dbError;

      toast.success("Attachment uploaded!");
      onUpload();
    } catch (error: any) {
      toast.error("Error uploading attachment.");
      console.error("Attachment upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="p-4 border-2 border-dashed border-muted rounded-md flex flex-col items-center hover:border-primary focus:outline-none transition-all cursor-pointer"
      onDrop={handleDrop}
      onDragOver={e => e.preventDefault()}
      tabIndex={0}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        disabled={uploading}
        onChange={handleFileChange}
      />
      <Upload className="h-8 w-8 mb-2 text-primary" />
      <span className="font-medium">{uploading ? "Uploading..." : "Drag & drop or click to upload"}</span>
    </div>
  );
};
